import React, { useContext, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { useListAccountsQuery } from 'hooks/queries/useListAccountsQuery';
import { LoaderComponent } from 'shared/LoaderComponent';
import { UserDetailsContext } from 'context/userDetailsContext';
import { dollarFormat } from 'utils/utils';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';
import { Button } from 'primereact/button';

/**
 * OperatingAccountingFiltersV2 Component
 * Filter component for Operating Accounting V2 with date range, account selection, and balance display
 *
 * @param {Object} props
 * @param {Date|Array} props.dateFilter - Current date filter value
 * @param {Function} props.onDateFilterChange - Handler for date filter changes
 * @param {string|null} props.selectedAccount - Selected account ID
 * @param {Function} props.onAccountChange - Handler for account changes
 * @param {Array} props.selectedSourceAccounts - Selected source accounts
 * @param {Function} props.onSourceAccountsChange - Handler for source accounts changes
 * @param {Array} props.selectedDestinationAccounts - Selected destination accounts
 * @param {Function} props.onDestinationAccountsChange - Handler for destination accounts changes
 * @param {Function} props.refreshData - Handler for resetting filters
 * @param {boolean} props.isFromCaseSpecificModule - Whether in case-specific view
 * @param {number} props.operatingBalance - Operating account balance
 */
const OperatingAccountingFiltersV2 = ({
  dateFilter,
  onDateFilterChange,
  selectedAccount,
  onAccountChange,
  selectedSourceAccounts,
  onSourceAccountsChange,
  selectedDestinationAccounts,
  onDestinationAccountsChange,
  refreshData,
  isFromCaseSpecificModule,
  operatingBalance = 0,
}) => {
  const userContext = useContext(UserDetailsContext);

  // Fetch operating accounts
  const { accountList: operatingAccountList, isAccountLoading: isOperatingAccountsLoading } = useListAccountsQuery({
    accountType: 'operating',
    excludeFromLoading: false,
    enabled: !isFromCaseSpecificModule,
  });

  // Fetch trust accounts for source/destination filters
  const { accountList: trustAccountList, isAccountLoading: isTrustAccountsLoading } = useListAccountsQuery({
    accountType: 'trust',
    excludeFromLoading: false,
  });

  const trustOptionswithExternal = useMemo(() => {
    return [
      { label: 'External', value: 'external_source' },
      ...(trustAccountList || []).map((account) => ({ label: account.account_name, value: account.account_id })),
    ];
  }, [trustAccountList]);

  const accountOptions = useMemo(() => {
    return operatingAccountList || [];
  }, [operatingAccountList]);
  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-2 w-100 align-items-center justify-content-between">
      <div className="d-flex flex-wrap align-items-center p-2 filter-wraper">
        {(selectedAccount || isFromCaseSpecificModule) && (
          <label className="p-0 m-0 filter-label">
            Balance: <span className="text-bold black-600">{dollarFormat(operatingBalance)}</span>
          </label>
        )}
      </div>
      <div className="d-block d-lg-none w-100">
        <Button
          className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-controls="operating-accounting-filter-section"
          label="Filters"
        >
          <span
            style={{
              fontSize: 18,
              transition: 'transform 0.2s',
              display: 'inline-block',
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <i className="fa fa-solid fa-angle-down"></i>
          </span>
        </Button>
      </div>
      <div
        className={`d-flex flex-wrap align-items-center gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
        id="operating-accounting-filter-section"
        aria-hidden={!showFilters}
      >
        <label className="text-bold black-600 p-0 m-0 filter-label">Filter:</label>
        <div className="d-flex flex-column filter-wraper">
          <TimezoneDatePicker
            className="input-shadow filter-calender"
            id="icon"
            name="dateFilter"
            selectionMode="range"
            value={dateFilter}
            onChange={onDateFilterChange}
            showIcon
            placeholder="Date"
            timezone={userContext?.userDetails?.timezone}
            isEndOfDay={true}
          />
        </div>
        {!isFromCaseSpecificModule && (
          <>
            <div className="d-flex flex-column filter-wraper">
              <Dropdown
                placeholder="By Account"
                options={accountOptions}
                value={selectedAccount}
                onChange={(e) => onAccountChange(e.value)}
                optionLabel="account_name"
                optionValue="account_id"
                className="w-100"
                filter
                showClear
                panelFooterTemplate={isOperatingAccountsLoading ? LoaderComponent : null}
              />
            </div>
            <div className="d-flex flex-column filter-wraper">
              <MultiSelect
                placeholder="Source"
                options={trustOptionswithExternal}
                value={selectedSourceAccounts}
                onChange={(e) => onSourceAccountsChange(e.value)}
                optionLabel="label"
                optionValue="value"
                className="w-100"
                filter
                showClear
                panelFooterTemplate={isTrustAccountsLoading ? LoaderComponent : null}
              />
            </div>
            <div className="d-flex flex-column filter-wraper">
              <MultiSelect
                placeholder="Destination"
                options={trustOptionswithExternal}
                value={selectedDestinationAccounts}
                onChange={(e) => onDestinationAccountsChange(e.value)}
                optionLabel="label"
                optionValue="value"
                className="w-100"
                filter
                showClear
                panelFooterTemplate={isTrustAccountsLoading ? LoaderComponent : null}
              />
            </div>
          </>
        )}
        <div className="d-flex flex-column mx-2">
          <div className="pointer ms-0">
            <i className="fas fa-sync-alt" onClick={refreshData}></i>
            <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatingAccountingFiltersV2;
